import type { AiFieldConfig } from '@databases/types/database';

export type AiFieldCellRequestTarget = {
  contentId: string;
  aiFieldType: AiFieldConfig['aiFieldType'];
  fieldId: string;
  entryId: string;
};

export type AiFieldCellRequestState =
  | { status: 'idle' }
  | { status: 'queued' }
  | { status: 'running' };

export type EnqueueAiFieldCellRequestResult =
  | { kind: 'already-pending' }
  | { kind: 'enqueued'; completion: Promise<void> };

type AiFieldCellRequest = {
  key: string;
  run: () => Promise<void>;
  resolve: () => void;
  reject: (error: unknown) => void;
};

type AiFieldCellRequestListener = () => void;
type AiFieldCellRequestOutcome =
  | { kind: 'completed' }
  | { kind: 'failed'; error: unknown };

const IDLE_REQUEST_STATE: AiFieldCellRequestState = { status: 'idle' };

const getRequestKey = ({
  contentId,
  aiFieldType,
  fieldId,
  entryId
}: AiFieldCellRequestTarget): string =>
  JSON.stringify([contentId, aiFieldType, fieldId, entryId]);

/**
 * Coordinates AI field cell requests within the current browser. It reserves cells
 * synchronously to prevent duplicate requests, processes different cells in FIFO order,
 * and waits for the existing database stream to become available before each request.
 * State subscriptions are scoped to a cell so only affected cells rerender. This does
 * not coordinate requests started by other collaborators.
 */
export class AiFieldCellRequestCoordinator {
  private readonly listenersByRequestKey = new Map<
    string,
    Set<AiFieldCellRequestListener>
  >();
  private readonly requestStates = new Map<string, AiFieldCellRequestState>();
  private readonly queue: AiFieldCellRequest[] = [];
  private readonly waitUntilReadyToRun: () => Promise<void>;
  private isRunning = false;

  public constructor(waitUntilReadyToRun: () => Promise<void> = () => Promise.resolve()) {
    this.waitUntilReadyToRun = waitUntilReadyToRun;
  }

  public subscribe(
    target: AiFieldCellRequestTarget,
    listener: AiFieldCellRequestListener
  ): () => void {
    const key = getRequestKey(target);
    const listeners = this.listenersByRequestKey.get(key) ?? new Set();
    listeners.add(listener);
    this.listenersByRequestKey.set(key, listeners);

    return () => {
      listeners.delete(listener);
      if (listeners.size === 0) {
        this.listenersByRequestKey.delete(key);
      }
    };
  }

  public getRequestState(target: AiFieldCellRequestTarget): AiFieldCellRequestState {
    return this.requestStates.get(getRequestKey(target)) ?? IDLE_REQUEST_STATE;
  }

  public enqueue({
    target,
    run
  }: {
    target: AiFieldCellRequestTarget;
    run: () => Promise<void>;
  }): EnqueueAiFieldCellRequestResult {
    const key = getRequestKey(target);
    if (this.requestStates.has(key)) {
      return { kind: 'already-pending' };
    }

    let resolve!: () => void;
    let reject!: (error: unknown) => void;
    const completion = new Promise<void>((resolvePromise, rejectPromise) => {
      resolve = resolvePromise;
      reject = rejectPromise;
    });

    this.requestStates.set(key, { status: 'queued' });
    this.queue.push({ key, run, resolve, reject });
    this.notify(key);
    this.runNextRequest();

    return { kind: 'enqueued', completion };
  }

  private runNextRequest(): void {
    if (this.isRunning) {
      return;
    }

    const request = this.queue.shift();
    if (!request) {
      return;
    }

    this.isRunning = true;
    void this.runRequest(request);
  }

  private async runRequest(request: AiFieldCellRequest): Promise<void> {
    try {
      await this.waitUntilReadyToRun();
      this.requestStates.set(request.key, { status: 'running' });
      this.notify(request.key);
      await request.run();
    } catch (error: unknown) {
      this.completeRequest(request, { kind: 'failed', error });
      return;
    }

    this.completeRequest(request, { kind: 'completed' });
  }

  private completeRequest(
    request: AiFieldCellRequest,
    outcome: AiFieldCellRequestOutcome
  ): void {
    this.requestStates.delete(request.key);
    this.isRunning = false;
    this.notify(request.key);
    this.runNextRequest();

    switch (outcome.kind) {
      case 'completed':
        request.resolve();
        break;
      case 'failed':
        request.reject(outcome.error);
        break;
      default: {
        const unhandledOutcome: never = outcome;
        throw new Error(`Unhandled AI field request outcome: ${unhandledOutcome}`);
      }
    }
  }

  private notify(key: string): void {
    this.listenersByRequestKey.get(key)?.forEach((listener) => listener());
  }
}
